import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import Button from '@components/common/buttons/Button';
import CheckBox from '@components/common/CheckBox';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import colors from '@utils/colors';
import { RegularText } from '@utils/typography';

import { STANDALONE_SECTION_KEY, categorySectionKey, sourceSectionKey } from './constants';
import {
  BulkBar,
  BulkBarClip,
  BulkBarInner,
  BulkError,
  CardCount,
  CardHead,
  CardHint,
  CardLink,
  CardList,
  CardStatus,
  CardCollapse,
  CardTitle,
  CatTitleName,
  DefaultQuestion,
  DoneSummary,
  EstimatorCard,
  HeaderActions,
  IncludeLabel,
  IncludeRow,
  ListBody,
  ListQuestion,
  ListToggle,
  ListToolbarLabel,
  ListToolbarRow,
  ListToolbarSelect,
  PickerActions,
  PickerActionsLeft,
  QuestionRow,
  ReadingsGroup,
  ReadingsGroupLabel,
  ResetLink,
  StatusFill,
  StatusTrack,
  Sep,
  TextBtn
} from './TaskEstimator.styles';
import {
  CategoryEmoji,
  CategoryPickerPanel,
  ConvertPickerPanel,
  CustomDurationPop,
  CustomPagesPop,
  NameField,
  PagePills,
  TimePills
} from './EstimatorControls';
import { SourceRateRow, TaskRows } from './EstimatorTaskRow';
import { formatPillMinutes, isTaskEstimated, parsePagesInput, plural, recommendedMinutes, timePillValues } from './utils';
import { EstimatorCategory, EstimatorReadingSource, EstimatorTask } from './types';
import { TaskEstimatorModel } from './useTaskEstimator';

const AutoCategorizeButton = styled(Button)`
  flex-shrink: 0;
  margin-top: 12px;
`;

const groupSelection = (ids: number[], selectedIds: number[]) => {
  const selectedCount = ids.filter(id => selectedIds.includes(id)).length;
  return { all: ids.length > 0 && selectedCount === ids.length, some: selectedCount > 0 && selectedCount < ids.length };
};

// The open list's first row: select all, and the card's reset when there is anything to reset.
const ListToolbar = ({
  ids,
  kind,
  model,
  action
}: {
  ids: number[];
  kind: 'regular' | 'reading';
  model: TaskEstimatorModel;
  action?: ReactNode;
}) => {
  const sel = groupSelection(ids, model.state.ui.selectedIds);
  const toggle = () => model.dispatch({ type: 'SELECT_GROUP', ids, kind });
  return (
    <ListToolbarRow>
      <ListToolbarSelect>
        <CheckBox checked={sel.all} indeterminate={sel.some} onChange={toggle} />
        <ListToolbarLabel onClick={toggle}>Select all</ListToolbarLabel>
      </ListToolbarSelect>
      {action}
    </ListToolbarRow>
  );
};

// The task list as a plain accordion: the row's label and chevron flip, the list opens under it, and the rest of
// the card stays as it is.
const TaskListAccordion = ({
  open,
  onToggle,
  noun,
  children
}: {
  open: boolean;
  onToggle: () => void;
  noun: string;
  children: ReactNode;
}) => (
  <CardList>
    <ListToggle type="button" aria-expanded={open} onClick={onToggle}>
      <MaterialIcon name={open ? 'expand_less' : 'expand_more'} size={18} color="currentColor" />
      {open ? 'Hide' : 'Show'} {noun}s
    </ListToggle>
    {open && <ListBody>{children}</ListBody>}
  </CardList>
);

// How long a picked card stays open, its pill green and its bar filling, before it slides shut.
const DONE_FOLD_DELAY_MS = 400;

// A card's status under its title, in plain numbers: "1/2 tasks estimated · Total workload 2h", the total of the
// times picked so far.
const CategoryStatus = ({ tasks }: { tasks: EstimatorTask[] }) => {
  const estimated = tasks.filter(isTaskEstimated);
  const done = estimated.length === tasks.length;
  const total = formatPillMinutes(Math.round(estimated.reduce((sum, task) => sum + (task.minutes || 0), 0)));
  return (
    <CardStatus $done={done}>
      <StatusTrack>
        <StatusFill $pct={tasks.length ? (estimated.length / tasks.length) * 100 : 100} />
      </StatusTrack>
      <span>
        <b>
          {estimated.length}/{tasks.length}
        </b>{' '}
        {tasks.length === 1 ? 'task' : 'tasks'} estimated
        {estimated.length > 0 && (
          <>
            {' · Total workload '}
            <b>{total}</b>
          </>
        )}
      </span>
    </CardStatus>
  );
};

const doneSummary = (tasks: EstimatorTask[], noun = 'task') => {
  const total = formatPillMinutes(Math.round(tasks.reduce((sum, task) => sum + (task.minutes || 0), 0))) || '0h';
  const times = new Set(tasks.map(task => task.minutes));
  const each = tasks.length > 1 && times.size === 1 ? `${formatPillMinutes(tasks[0].minutes!)} each · ` : '';
  return `${each}${plural(tasks.length, noun)} · ${total}`;
};

// One wording for every kind of task (Petr, 2026-10-08): "study time" covers studying for a test and working on
// anything else alike.
const questionFor = (taskCount: number) => `How much study time will you need for ${taskCount === 1 ? 'it' : 'each'}?`;

export const CategorySection = ({
  category,
  tasks,
  model,
  current
}: {
  category: EstimatorCategory;
  tasks: EstimatorTask[];
  model: TaskEstimatorModel;
  current: boolean;
}) => {
  const { state, dispatch, categoryHasAnyValue, finishedInCategory } = model;
  // A finished card folds to its status. "Change" opens all of it at once, the question and the task list, and
  // "Close" folds it again. A pick folds it too, after a moment that shows the green pill and the filling bar.
  const [expanded, setExpanded] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  // Ticked by default (Petr, 2026-10-28): a pick is the category's time, so it re-estimates every task. Unticked, it
  // fills the tasks without a time and leaves earlier estimates alone.
  const [includeEstimated, setIncludeEstimated] = useState(true);

  useEffect(() => {
    if (!holdOpen) return undefined;
    const timer = window.setTimeout(() => setHoldOpen(false), DONE_FOLD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [holdOpen]);

  const active = tasks.filter(task => !task.trashed);
  const estimatedCount = active.filter(isTaskEstimated).length;
  const done = estimatedCount === active.length;
  const finished = finishedInCategory(category.id);
  // Under the pills, one caption: "recommended" from what finished tasks really took, which beats a past guess, or
  // else "last estimate", the time most of them were given before.
  const mark = finished
    ? {
        minutes: recommendedMinutes(finished.minutes / finished.count),
        label: 'recommended',
        hover:
          finished.count === 1
            ? `The one you finished took ${formatPillMinutes(Math.round(finished.minutes))}.`
            : `The ${finished.count} you finished took ${formatPillMinutes(
                Math.round(finished.minutes / finished.count)
              )} on average.`
      }
    : category.priorMinutes != null
    ? { minutes: category.priorMinutes, label: 'last estimate' }
    : null;
  const sectionKey = categorySectionKey(category.id);
  const listOpen = !state.ui.collapsedSectionKeys.includes(sectionKey);
  const isCustom = category.defaultMinutes != null && !timePillValues(mark?.minutes).includes(category.defaultMinutes);
  const customOpen = isCustom || state.ui.openCustomKeys.includes(`catdef-${category.id}`);
  const editing = state.ui.editingName?.type === 'category' && state.ui.editingName.id === category.id;
  const toggleList = () => dispatch({ type: 'TOGGLE_COLLAPSE', key: sectionKey });
  const showDone = done && !expanded && !holdOpen;
  // The box only matters while some tasks have a time and some don't; a finished card re-estimates them all.
  const offerInclude = !done && estimatedCount > 0;
  const onlyUnestimated = offerInclude && !includeEstimated;

  const openCard = () => {
    setExpanded(true);
    if (!listOpen) toggleList();
  };

  const pick = (minutes: number) => {
    dispatch({ type: 'APPLY_CATEGORY_DEFAULT', categoryId: category.id, minutes, onlyUnestimated });
    setExpanded(false);
    setHoldOpen(true);
  };

  const list = (
    <TaskListAccordion open={listOpen} onToggle={toggleList} noun="task">
      <ListToolbar
        ids={active.map(task => task.id)}
        kind="regular"
        model={model}
        action={
          categoryHasAnyValue(category) && (
            <TextBtn type="button" onClick={() => dispatch({ type: 'RESET_CATEGORY', categoryId: category.id })}>
              Reset all
            </TextBtn>
          )
        }
      />
      <TaskRows tasks={tasks} category={category} model={model} />
    </TaskListAccordion>
  );

  // Folded, the title is plain: a click anywhere on the card opens it, rather than renaming the category or picking
  // its emoji, which work again once it's open.
  const title = (
    <CardTitle style={showDone ? { pointerEvents: 'none' } : undefined}>
      {done && <MaterialIcon name="check_circle" size={18} color={colors.positive} />}
      <CategoryEmoji category={category} dispatch={dispatch} />
      <NameField type="category" id={category.id} name={category.name} editing={editing} catTitle dispatch={dispatch} />
    </CardTitle>
  );

  // A finished card opens from anywhere on it; "Change" stays as the sign of what a click does.
  const openFromCard = showDone
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-expanded': false,
        onClick: openCard,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openCard();
        }
      }
    : {};

  return (
    <EstimatorCard $current={current && !done} $done={showDone} $clickable={showDone} {...openFromCard}>
      <CardHead>
        {title}
        {showDone ? (
          <CardLink as="span" aria-hidden>
            Change ▾
          </CardLink>
        ) : (
          done &&
          expanded && (
            <CardLink type="button" aria-expanded onClick={() => setExpanded(false)}>
              Close ▴
            </CardLink>
          )
        )}
      </CardHead>
      <CategoryStatus tasks={active} />
      <CardCollapse $open={!showDone} aria-hidden={showDone}>
        <div>
          <QuestionRow>
            <DefaultQuestion>{questionFor(active.length)}</DefaultQuestion>
            {offerInclude && (
              <IncludeRow>
                <CheckBox checked={includeEstimated} onChange={() => setIncludeEstimated(value => !value)} />
                <IncludeLabel onClick={() => setIncludeEstimated(value => !value)}>
                  Also re-estimate the {estimatedCount} previously estimated {estimatedCount === 1 ? 'task' : 'tasks'}
                </IncludeLabel>
              </IncludeRow>
            )}
            <TimePills
              currentMinutes={category.defaultMinutes}
              selected
              positive
              mark={mark}
              onPreset={pick}
              onCustomToggle={() => dispatch({ type: 'TOGGLE_CUSTOM', key: `catdef-${category.id}` })}
            />
            {customOpen && <CustomDurationPop value={category.defaultMinutes} onApply={pick} />}
          </QuestionRow>
          {list}
        </div>
      </CardCollapse>
    </EstimatorCard>
  );
};

export const SourceSection = ({
  source,
  tasks,
  model,
  current
}: {
  source: EstimatorReadingSource;
  tasks: EstimatorTask[];
  model: TaskEstimatorModel;
  current: boolean;
}) => {
  const { state, dispatch, sourceHasChangeFromDetected } = model;
  const active = tasks.filter(task => !task.trashed);
  const doneCount = active.filter(isTaskEstimated).length;
  const done = doneCount === active.length;
  const key = sourceSectionKey(source.id);
  const collapsed = state.ui.collapsedSectionKeys.includes(key);
  const editing = state.ui.editingName?.type === 'source' && state.ui.editingName.id === source.id;
  const needsPages = active.some(task => !isTaskEstimated(task));
  const toggleList = () => dispatch({ type: 'TOGGLE_COLLAPSE', key });

  // Each reading takes its own page count, so the list stays open. Only a book whose pages were all found in the
  // syllabus folds it, and even then keeps its time per page open: import gives every book the same 5 min a page and
  // counts found pages as estimated, so this is often the student's first look at it.
  return (
    <EstimatorCard $current={current} $done={done}>
      <CardHead>
        <CardTitle>
          {done && <MaterialIcon name="check_circle" size={18} color={colors.positive} />}
          <NameField type="source" id={source.id} name={source.name} editing={editing} catTitle dispatch={dispatch} />
        </CardTitle>
        <HeaderActions>
          {done ? (
            <DoneSummary>{doneSummary(active, 'reading')}</DoneSummary>
          ) : (
            <CardCount>
              {doneCount} of {plural(active.length, 'reading')} set
            </CardCount>
          )}
        </HeaderActions>
      </CardHead>
      <SourceRateRow source={source} model={model} />
      {needsPages && <ListQuestion>How many pages is each reading?</ListQuestion>}
      <TaskListAccordion open={!collapsed} onToggle={toggleList} noun="reading">
        <ListToolbar
          ids={active.map(task => task.id)}
          kind="reading"
          model={model}
          action={
            sourceHasChangeFromDetected(source.id) && (
              <TextBtn type="button" onClick={() => dispatch({ type: 'RESET_SOURCE_DETECTED', sourceId: source.id })}>
                Reset to detected
              </TextBtn>
            )
          }
        />
        <TaskRows tasks={tasks} model={model} />
      </TaskListAccordion>
    </EstimatorCard>
  );
};

export const ReadingsGroupSection = ({ model, currentKey }: { model: TaskEstimatorModel; currentKey: string | null }) => {
  const { state, readingTasks, tasksBySource } = model;
  if (state.sources.length === 0 && readingTasks.length === 0) return null;
  const active = readingTasks.filter(task => !task.trashed);
  const doneCount = active.filter(isTaskEstimated).length;
  return (
    <ReadingsGroup>
      <ReadingsGroupLabel>
        <DefaultQuestion as="span">Readings</DefaultQuestion>
        <CardCount>
          {doneCount} of {plural(active.length, 'reading')} set
        </CardCount>
      </ReadingsGroupLabel>
      {state.sources.map(source => (
        <SourceSection
          key={source.id}
          source={source}
          tasks={tasksBySource(source.id)}
          model={model}
          current={currentKey === sourceSectionKey(source.id)}
        />
      ))}
    </ReadingsGroup>
  );
};

export const StandaloneSection = ({
  model,
  onAutoCategorize,
  isCategorizing,
  current
}: {
  model: TaskEstimatorModel;
  onAutoCategorize?: () => void;
  isCategorizing?: boolean;
  current: boolean;
}) => {
  const { state, dispatch, standaloneTasks } = model;
  if (standaloneTasks.length === 0) return null;
  const active = standaloneTasks.filter(task => !task.trashed);
  const doneCount = active.filter(isTaskEstimated).length;
  const collapsed = state.ui.collapsedSectionKeys.includes(STANDALONE_SECTION_KEY);
  const hasAnyValue = standaloneTasks.some(task => task.minutes != null || task.overridden || task.trashed);
  const needsTimes = active.some(task => !isTaskEstimated(task));
  const toggleList = () => dispatch({ type: 'TOGGLE_COLLAPSE', key: STANDALONE_SECTION_KEY });

  // These share no category, so each takes its own time and the list stays open.
  return (
    <EstimatorCard $current={current}>
      <CardHead>
        <CardTitle>
          <CatTitleName as="span">Uncategorized tasks</CatTitleName>
        </CardTitle>
        <HeaderActions>
          <CardCount>
            {doneCount} of {plural(active.length, 'task')} set
          </CardCount>
        </HeaderActions>
      </CardHead>
      {needsTimes && (
        <QuestionRow>
          <DefaultQuestion>How long will each of these take?</DefaultQuestion>
          <CardHint>
            They have no category to share a time
            {onAutoCategorize ? ', so give each its own, or sort them into categories first.' : '.'}
          </CardHint>
          {onAutoCategorize && (
            <AutoCategorizeButton size="sm" noMinWidth disabled={isCategorizing} onClick={onAutoCategorize}>
              {isCategorizing ? 'Categorizing…' : `Auto-categorize ${plural(active.length, 'task')}`}
            </AutoCategorizeButton>
          )}
        </QuestionRow>
      )}
      <TaskListAccordion open={!collapsed} onToggle={toggleList} noun="task">
        <ListToolbar
          ids={active.map(task => task.id)}
          kind="regular"
          model={model}
          action={
            hasAnyValue && (
              <TextBtn type="button" onClick={() => dispatch({ type: 'RESET_STANDALONE' })}>
                Reset all
              </TextBtn>
            )
          }
        />
        <TaskRows tasks={standaloneTasks} model={model} />
      </TaskListAccordion>
    </EstimatorCard>
  );
};

export const EstimatorBulkBar = ({ model }: { model: TaskEstimatorModel }) => {
  const { state, dispatch, selectedType, readingWasOverridden } = model;
  const n = state.ui.selectedIds.length;
  const open = n > 0 || !!state.ui.selectionError;

  const noun = selectedType === 'reading' ? (n === 1 ? 'reading' : 'readings') : n === 1 ? 'task' : 'tasks';
  const selectedIds = state.ui.selectedIds;
  const isCustomOpen = state.ui.openCustomKeys.includes('bulk');
  const isPagesCustomOpen = state.ui.openCustomKeys.includes('bulk-pages');
  const hasCategoryTask = selectedIds.some(id => {
    const task = state.tasks.find(item => item.id === id);
    if (!task || task.categoryId == null) return false;
    const category = state.categories.find(item => item.id === task.categoryId);
    return category?.defaultMinutes != null;
  });
  const hasOverriddenReading = selectedIds.some(id => {
    const task = state.tasks.find(item => item.id === id);
    return task ? readingWasOverridden(task) : false;
  });

  return (
    <BulkBar $open={open}>
      <BulkBarClip>
        <BulkBarInner $open={open}>
          {state.ui.selectionError && <BulkError>{state.ui.selectionError}</BulkError>}
          {n > 0 && (
            <>
              <RegularText strong style={{ width: '100%' }}>
                {n} {noun} selected
              </RegularText>
              {selectedType === 'reading' ? (
                <>
                  <PagePills
                    currentPages={null}
                    onPreset={pages =>
                      dispatch({
                        type: 'BULK_SET_PAGES',
                        pages,
                        from: null,
                        to: null,
                        rangeLabel: `${pages}p`,
                        rawPagesInput: String(pages)
                      })
                    }
                    onCustomToggle={() => dispatch({ type: 'TOGGLE_CUSTOM', key: 'bulk-pages' })}
                    trailing={
                      <ResetLink type="button" onClick={() => dispatch({ type: 'CLEAR_SELECTION' })}>
                        Clear
                      </ResetLink>
                    }
                  />
                  {isPagesCustomOpen && (
                    <CustomPagesPop
                      value=""
                      onApply={raw => {
                        const parsed = parsePagesInput(raw);
                        if (!parsed) return false;
                        dispatch({
                          type: 'BULK_SET_PAGES',
                          pages: parsed.pages,
                          from: parsed.from ?? null,
                          to: parsed.to ?? null,
                          rangeLabel: parsed.rangeLabel,
                          rawPagesInput: raw.trim()
                        });
                        return true;
                      }}
                    />
                  )}
                </>
              ) : (
                <>
                  <TimePills
                    currentMinutes={null}
                    selected={false}
                    onPreset={minutes => dispatch({ type: 'BULK_SET_MINUTES', minutes })}
                    onCustomToggle={() => dispatch({ type: 'TOGGLE_CUSTOM', key: 'bulk' })}
                    trailing={
                      <ResetLink type="button" onClick={() => dispatch({ type: 'CLEAR_SELECTION' })}>
                        Clear
                      </ResetLink>
                    }
                  />
                  {isCustomOpen && (
                    <CustomDurationPop value={null} onApply={minutes => dispatch({ type: 'BULK_SET_MINUTES', minutes })} />
                  )}
                </>
              )}
              <PickerActions style={{ width: '100%', marginTop: 0 }}>
                <PickerActionsLeft>
                  {selectedType === 'reading' && hasOverriddenReading && (
                    <>
                      <ResetLink type="button" onClick={() => dispatch({ type: 'BULK_RESTORE_DETECTED' })}>
                        Restore detected
                      </ResetLink>
                      <Sep>·</Sep>
                    </>
                  )}
                  {selectedType !== 'reading' && hasCategoryTask && (
                    <>
                      <ResetLink type="button" onClick={() => dispatch({ type: 'BULK_USE_DEFAULT' })}>
                        Use category default
                      </ResetLink>
                      <Sep>·</Sep>
                    </>
                  )}
                  {selectedType !== 'reading' && state.categories.length > 0 && (
                    <>
                      <ResetLink type="button" onClick={() => dispatch({ type: 'TOGGLE_BULK_MOVE_CATEGORY' })}>
                        Move to category
                      </ResetLink>
                      <Sep>·</Sep>
                    </>
                  )}
                  <ResetLink type="button" onClick={() => dispatch({ type: 'TOGGLE_BULK_CONVERT' })}>
                    {selectedType === 'reading' ? 'Estimate by time instead' : 'Estimate by pages instead'}
                  </ResetLink>
                </PickerActionsLeft>
                <ResetLink type="button" onClick={() => dispatch({ type: 'BULK_TRASH' })}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <MaterialIcon name="delete_outline" size={13} /> Trash
                  </span>
                </ResetLink>
              </PickerActions>
              {state.ui.bulkConverting && (
                <ConvertPickerPanel
                  isReading={selectedType === 'reading'}
                  plural
                  categories={state.categories}
                  sources={state.sources}
                  onRegular={categoryId => dispatch({ type: 'BULK_CONVERT_TO_REGULAR', categoryId })}
                  onReading={sourceId => dispatch({ type: 'BULK_CONVERT_TO_READING', sourceId })}
                />
              )}
              {state.ui.bulkMovingCategory && selectedType !== 'reading' && (
                <CategoryPickerPanel
                  plural
                  categories={state.categories}
                  onSelect={categoryId => dispatch({ type: 'BULK_REASSIGN_CATEGORY', categoryId })}
                />
              )}
            </>
          )}
        </BulkBarInner>
      </BulkBarClip>
    </BulkBar>
  );
};
